'use client'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { MatrixItem, SizeItem } from '@/types/size'
import { ExpandIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

type Props = {
  headerTitle?: string
  leftTitle?: string
  columns: SizeItem[]
  rows: SizeItem[]
  matrix: MatrixItem[]
  suffixCol?: string
  className?: string
  colClassName?: string
  title?: string
  itemClassName?: string
  productId?: string
  note?: string
}

export const TableSize: React.FC<Props> = ({
  columns,
  rows,
  matrix,
  headerTitle,
  leftTitle,
  suffixCol,
  className,
  colClassName,
  title,
  itemClassName,
  productId,
  note,
}) => {
  const [hovered, setHovered] = useState<{ row: number; col: number } | null>(
    null
  )
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const t = useTranslations('button')

  // Helper function to find the size for a given rowId, columnId
  const getSizeForCoordinate = (rowId: number, columnId: number): string => {
    const sizeEntry = matrix.find(
      (item) => item.rowId === rowId && item.columnId === columnId
    )
    return sizeEntry?.value || ''
  }

  // Hàm render table để sử dụng ở cả 2 nơi
  const renderTable = (showExpandButton = false) => {
    return (
      <>
        {headerTitle && (
          <div className='h-7 border-b border-neutral-200 bg-neutral-50 px-2 py-1 text-center font-criteria text-sm uppercase leading-5 text-neutral-500'>
            {headerTitle}
          </div>
        )}
        <div
          className={cn(
            'overflow-x-auto',
            leftTitle && 'grid grid-cols-[auto_1fr]'
          )}
        >
          {leftTitle && (
            <div className='sticky left-0 z-10 flex w-7 items-center justify-center border-r border-neutral-200 bg-neutral-50 px-1 py-1 text-center font-criteria text-sm leading-5 text-neutral-500'>
              <p className='absolute -left-8 w-max -rotate-90 uppercase'>
                {leftTitle}
              </p>
            </div>
          )}
          <div className='relative overflow-x-auto'>
            <table className='relative w-full border border-neutral-200 font-criteria'>
              <thead>
                <tr className='border-b border-neutral-200 bg-light'>
                  <th
                    className={cn(
                      'sticky left-0 z-10 min-w-20 bg-light p-2 text-center text-sm font-bold leading-5 text-neutral-800',
                      colClassName
                    )}
                  >
                    {suffixCol}
                    {showExpandButton && (
                      <Button
                        variant='ghost'
                        size='icon'
                        className='h-4 w-4 hover:scale-110 hover:bg-transparent hover:text-black'
                        onClick={() => setIsDialogOpen(true)}
                      >
                        <ExpandIcon />
                      </Button>
                    )}
                  </th>
                  {columns.map((column) => (
                    <th
                      key={column.id}
                      className={cn(
                        'min-w-20 p-2 text-center text-sm font-bold leading-5 text-neutral-800',
                        hovered?.col === column.id && 'bg-neutral-200/70',
                        itemClassName
                      )}
                    >
                      {column.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-b border-b-neutral-200 bg-light',
                      index % 2 === 0 && 'bg-neutral-100'
                    )}
                  >
                    <td
                      className={cn(
                        'sticky left-0 z-10 whitespace-pre-wrap p-2 text-center text-sm font-bold text-neutral-800',
                        hovered?.row === row.id && 'bg-neutral-200/70',
                        index % 2 === 0 ? 'bg-neutral-100' : 'bg-light',
                        colClassName
                      )}
                    >
                      {row.name}
                    </td>
                    {columns.map((column) => {
                      const size = getSizeForCoordinate(row.id, column.id)

                      const rowIndex = rows.findIndex((r) => r.id === row.id)
                      const colIndex = columns.findIndex(
                        (c) => c.id === column.id
                      )
                      const hoveredRowIndex = hovered
                        ? rows.findIndex((r) => r.id === hovered.row)
                        : -1
                      const hoveredColIndex = hovered
                        ? columns.findIndex((c) => c.id === hovered.col)
                        : -1

                      const isInColumnRange =
                        hovered &&
                        colIndex === hoveredColIndex &&
                        rowIndex <= hoveredRowIndex

                      const isInRowRange =
                        hovered &&
                        rowIndex === hoveredRowIndex &&
                        colIndex <= hoveredColIndex

                      const shouldHighlight = isInColumnRange || isInRowRange

                      return (
                        <td
                          key={column.id}
                          className={cn(
                            'p-2 text-center font-sans text-sm text-neutral-700 hover:bg-neutral-200 hover:text-primary',
                            shouldHighlight && 'bg-neutral-200/70'
                          )}
                          onMouseEnter={() =>
                            setHovered({ row: row.id, col: column.id })
                          }
                          onMouseLeave={() => setHovered(null)}
                        >
                          {size}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className={className}>
      {title && (
        <div className='mb-2 flex items-center justify-between'>
          <div>
            <div className='text-base font-bold uppercase'>{title}</div>
            {productId && productId === '688e0aa90f3e5f2880734e6c' ? (
              <div className='text-xs text-neutral-500'>
                {note ||
                  'Xem bảng size chi tiết của sản phẩm nam và nữ dưới phần mô tả'}
              </div>
            ) : null}
          </div>

          {/* icon mở rộng */}
          <Button
            variant='outline'
            size='small'
            className='text-xs'
            onClick={() => setIsDialogOpen(true)}
          >
            {t('expand')} <ExpandIcon />
          </Button>
        </div>
      )}

      {/* Render bảng chính */}
      {renderTable(!headerTitle && !suffixCol)}

      {/* Popup Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className='max-h-[90vh] overflow-y-auto md:max-w-[900px]'>
          <DialogHeader className='flex flex-row items-center justify-between'>
            <DialogTitle className='text-base font-bold uppercase'>
              {title}
            </DialogTitle>
          </DialogHeader>

          <div className='mt-4'>
            {/* Render bảng trong modal */}
            {renderTable(false)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
